// React imports
import React, { useEffect, useState } from 'react';

// Material UI imports
import { MenuItem, Box, SelectChangeEvent } from '@mui/material';

// Third-party imports
import { MRT_ColumnDef, MRT_RowData } from 'material-react-table';

// ERP Common imports
import Select from '@/components/select/select';
import TextField from '@/components/text-field/text-field';
import formatText from '@/utils/format-text';

// Local imports
import { IElementJson } from '../custom-form';
import { ApiFormFields } from '../../Redux/state';
import FieldSelect from './field-select';
import '../custom-form.scss';

interface ITableEditForm {
	handlePropertyValueUpdate: (
		event?: any,
		numberValue?: any,
		val?: any,
		fieldType?: any
	) => void;
	handleColumnsUpdate: (columns: MRT_ColumnDef<MRT_RowData>[]) => void;
	element: IElementJson | null;
	fields: ApiFormFields | null;
}

interface TableOption {
	label: string;
	value: string;
}

const TableEditForm: React.FC<ITableEditForm> = ({
	handlePropertyValueUpdate,
	element,
	handleColumnsUpdate,
	fields
}) => {
	/* Local States */
	const [elementJSON, setElementJSON] = useState<IElementJson | null>(element);
	const [tables, setTables] = useState<TableOption[]>([]);

	/* Local Functions */
	const handleFieldUpdate = (event: SelectChangeEvent<unknown>) => {
		if (elementJSON) {
			elementJSON.field_type = event.target.value as string;
			handlePropertyValueUpdate(null, null, null, event.target.value as string);
		}
	};
	const handleTableSelect = (event: SelectChangeEvent<unknown>) => {
		handlePropertyValueUpdate(event);
	};

	/* Use Effects */
	useEffect(() => {
		setElementJSON(element);
	}, [element]);
	useEffect(() => {
		if (elementJSON && elementJSON.table && fields) {
			const fieldsArr = fields[elementJSON.table];
			let columns: any[] = [];
			if (fieldsArr && fieldsArr?.length) {
				columns = fieldsArr.map((field, index) => ({
					header: formatText(field.field),
					accessorKey: field.field,
					visible: index > 3 ? false : true,
					type: field.type
				}));
			} else {
				columns = [
					{ header: 'Column 1', accessorKey: 'column_1', visible: true },
					{ header: 'Column 2', accessorKey: 'column_1', visible: true }
				];
			}
			columns && handleColumnsUpdate(columns);
		}
	}, [elementJSON?.table]);
	useEffect(() => {
		if (fields) {
			const options = Object.keys(fields).map((key) => ({
				label: formatText(key),
				value: key
			}));
			setTables(options);
		}
	}, [fields]);

	if (!elementJSON) {
		return null;
	}

	return (
		<Box className='formSection--FieldContainer' key={elementJSON.id}>
			<div key={elementJSON.id}>
				<FieldSelect
					key={elementJSON.id}
					variant='outlined'
					onChange={handleFieldUpdate}
					defaultValue={elementJSON.field_type}
					disabled={elementJSON.type === 'system' || !elementJSON.is_new}
				/>
			</div>
			<div key={elementJSON.id}>
				<TextField
					fullWidth
					label='Label'
					is_required
					key={elementJSON.id}
					name='label'
					type='text'
					onChange={handlePropertyValueUpdate}
					defaultValue={elementJSON.label}
				/>
			</div>

			<div key={elementJSON.id}>
				<Select
					key={elementJSON.id}
					variant='outlined'
					label='Table'
					fullWidth
					placeholder='Select Table'
					size='small'
					name='table'
					defaultValue={elementJSON.table}
					disabled={elementJSON.type === 'system'}
					onChange={handleTableSelect}
					renderValue={(value: string) =>
						tables.find((option) => option.value === value)?.label
					}>
					{tables?.map((item) => (
						<MenuItem value={item.value}>{item.label}</MenuItem>
					))}
				</Select>
			</div>
		</Box>
	);
};

export default TableEditForm;
